import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { count, hours as fmtHours, since } from '../lib/format.js';
import { sizeTierLabel } from '../lib/size.js';

/**
 * The six rungs and what each one publishes (Estimate Tool 8.10; Phase E14).
 *
 * `/estimate/settings/templates`, and — as a card with `compact` — on the Estimate settings
 * screen, which is where the design says the designer is reached from. One row per rung of the
 * size ladder whether or not it holds a version yet: a rung with nothing published is the one a
 * reader most needs to see.
 */
class EstimateTemplateList extends StoreMixin(LitElement) {
  static properties = {
    compact: { type: Boolean },
    _templates: { state: true },
    _error: { state: true },
  };

  constructor() {
    super();
    this.compact = false;
    this._templates = null;
    this._error = null;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
      max-width: 780px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 14px;
      color: var(--ink-muted);
      font-size: 13.5px;
      max-width: 68ch;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    th {
      text-align: left;
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
      padding: 6px 8px;
      border-bottom: 1px solid var(--border);
      white-space: nowrap;
    }
    td {
      padding: 8px;
      border-bottom: 1px solid var(--border-soft);
      vertical-align: top;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .tier {
      font-weight: 700;
      color: var(--ink-strong);
      text-transform: capitalize;
    }
    .n {
      display: block;
      font-size: 12px;
      color: var(--ink-muted);
    }
    .mark {
      font-family: var(--font-eyebrow);
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 1px 5px;
      border-radius: 4px;
      border: 1px solid var(--border-soft);
      color: var(--ink-muted);
      white-space: nowrap;
    }
    .mark.draft {
      color: var(--status-caution, var(--ink-strong));
      border-color: currentColor;
    }
    .mark.none {
      color: var(--status-overloaded);
      border-color: currentColor;
    }
    button {
      font: inherit;
      font-size: 12px;
      font-weight: 600;
      padding: 5px 11px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--surface);
      color: var(--ink-strong);
      cursor: pointer;
      white-space: nowrap;
    }
    .state,
    .error {
      color: var(--ink-muted);
      padding: 12px 0;
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      const data = await api.get('/estimate/templates');
      this._templates = data.templates;
      this._error = null;
    } catch (err) {
      this._error = `Could not load the templates: ${err.message}`;
    }
  }

  _row(t) {
    const p = t.published;
    return html`
      <tr>
        <td>
          <span class="tier">${sizeTierLabel(t.tier)}</span>
          <span class="n"
            >${t.size_range ? `${t.size_range.min}–${t.size_range.max} h on the size axis` : 'no size range yet'}</span
          >
        </td>
        <td>
          ${
            p
              ? html`version ${p.version_number}
                  <span class="n">
                    ${p.published_at ? `published ${since(p.published_at)}` : ''}${
                      p.published_by ? ` by ${p.published_by}` : ''
                    }${p.origin === 'imported' && p.source_project_number ? ` · imported from ${p.source_project_number}` : ''}
                  </span>`
              : html`<span class="mark none">Nothing published</span>
                  <span class="n"
                    >The generator cannot cut this rung until a version is published.</span
                  >`
          }
          ${t.draft ? html`<span class="mark draft" title="A draft is open on this rung">Draft v${t.draft.version_number}</span>` : nothing}
          ${
            t.needs_attention
              ? html`<span class="mark none" title=${t.needs_attention}>Needs attention</span>
                  <span class="n">${t.needs_attention}</span>`
              : nothing
          }
        </td>
        <td class="num">${p ? fmtHours(p.hours, { exact: true }) : '—'}</td>
        <td class="num">${p ? `${count(p.task_count)} / ${count(p.assignment_count)}` : '—'}</td>
        <td>
          <button @click=${() => navigate(`/estimate/settings/templates/${t.tier}`)}>
            ${t.draft ? 'Continue draft' : p ? 'Open' : 'Start'}
          </button>
        </td>
      </tr>
    `;
  }

  render() {
    if (this._error) return html`<div class="card"><p class="error">${this._error}</p></div>`;
    if (!this._templates) return html`<div class="card"><p class="state">Loading…</p></div>`;
    return html`
      <div class="card">
        <div class="eyebrow">Schedule templates</div>
        <h2>${this.compact ? 'What a schedule is cut from' : 'The template for each size'}</h2>
        <p class="lede">
          One template per rung of the size ladder. Create Schedule cuts from the published version;
          a draft changes nothing until it is published, and every plan remembers the version it
          came from.
          ${
            this.compact
              ? html` <button @click=${() => navigate('/estimate/settings/templates')}>
                  Open the designer
                </button>`
              : nothing
          }
        </p>
        <table>
          <thead>
            <tr>
              <th>Rung</th>
              <th>Published</th>
              <th class="num">Hours</th>
              <th class="num">Tasks / lines</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${this._templates.map((t) => this._row(t))}
          </tbody>
        </table>
      </div>
    `;
  }
}

customElements.define('estimate-template-list', EstimateTemplateList);
